import { PromotionKind } from '@lombok/shared';
import { CheckboxField, SelectField, TextField, TextareaField } from '@/components/form';
import { KIND_OPTIONS } from './labels';
import { promotionValueRule } from './promotionForm';
import type { PromotionFormErrors, PromotionFormValues } from './promotionForm';

/**
 * Fields shared by "Nueva promoción" and "Editar promoción". Each rule is shown under its own
 * field; `errors` comes from `validatePromotionForm` (and from the server for duplicate codes).
 */
export function PromotionFormFields({
  form,
  errors,
  onChange,
}: {
  form: PromotionFormValues;
  errors: PromotionFormErrors;
  onChange: <K extends keyof PromotionFormValues>(key: K, value: PromotionFormValues[K]) => void;
}) {
  const isFixed = form.kind === PromotionKind.FIXED;
  return (
    <>
      <TextField
        label="Nombre"
        value={form.name}
        onChange={(e) => onChange('name', e.target.value)}
        placeholder="Ej. Descuento de lanzamiento"
        required
        error={errors.name}
      />

      <TextareaField
        label="Descripción (opcional)"
        value={form.description}
        onChange={(e) => onChange('description', e.target.value)}
        placeholder="Detalles de la promoción"
      />

      <div className="grid gap-4 sm:grid-cols-2">
        <SelectField
          label="Tipo"
          value={form.kind}
          onChange={(e) => onChange('kind', e.target.value as PromotionKind)}
          options={KIND_OPTIONS}
        />
        <TextField
          label={isFixed ? 'Monto (USD)' : 'Porcentaje (%)'}
          inputMode="decimal"
          value={form.value}
          onChange={(e) => onChange('value', e.target.value)}
          placeholder={isFixed ? 'Ej. 50,00' : 'Ej. 15'}
          required
          error={errors.value}
          hint={promotionValueRule(form.kind)}
        />
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <TextField
          label="Inicio"
          type="datetime-local"
          value={form.startsAt}
          onChange={(e) => onChange('startsAt', e.target.value)}
          required
          error={errors.startsAt}
        />
        <TextField
          label="Fin"
          type="datetime-local"
          value={form.endsAt}
          onChange={(e) => onChange('endsAt', e.target.value)}
          required
          error={errors.endsAt}
        />
      </div>

      <TextField
        label="Código (opcional)"
        value={form.code}
        onChange={(e) => onChange('code', e.target.value)}
        placeholder="Ej. LANZAMIENTO15"
        error={errors.code}
        hint="De 3 a 40 caracteres: letras, números, guion o guion bajo."
      />

      <CheckboxField
        label="Activa"
        checked={form.active}
        onChange={(e) => onChange('active', e.target.checked)}
        hint="Si está activa y dentro de las fechas, los clientes la verán."
      />
    </>
  );
}
